// Site → Canvas launch link (owner request A, 5 Oct 2026).
//
// Opening the simulation editor FROM a Site must leave the Canvas linked to
// that Site (Site Configuration → Active Site, toolbar "Link site") instead of
// "No Site Defined". This module owns the decision; the Zustand adapter lives
// in `src/store/simulationSiteLaunch.ts` and every Site entry point calls it
// after it has activated the Canvas tab it opens.
//
// Rules:
//   - no link yet            → link to the launch Site (new/empty Canvas, or an
//                               existing unlinked simulation listed under it);
//   - already linked to it   → nothing to do;
//   - linked to ANOTHER site → keep it. An explicit link only changes through
//                               the user's own "Link site" action;
//   - read-only Canvas       → nothing (repair sources and viewer/reviewer
//                               revisions go through the store's single
//                               mutation gate, which also guards `setSite`).
// The Site value written is the caller's real SiteEntity projection (same id),
// so EO run persistence and `linkedSiteEntity` resolve to that Site.

import type { Site as EditorSite } from '../../../simulation/types';

/** Minimal view of the active Canvas tab the decision needs. */
export interface SiteLaunchCanvasTab {
  readonly id: string;
  readonly site: Pick<EditorSite, 'id' | 'name'> | null;
  /** Store-authoritative mutation blocker (`canvasTabMutationBlocker`), or null when editable. */
  readonly mutationBlocker: string | null;
}

export type SiteLaunchLinkDecision =
  | { readonly kind: 'link' }
  | { readonly kind: 'already-linked' }
  | {
      readonly kind: 'keep-explicit';
      readonly linkedSiteId: string;
      readonly linkedSiteName: string;
    }
  | { readonly kind: 'read-only'; readonly reason: string }
  | { readonly kind: 'no-canvas' };

/** Decide what opening `launchSiteId` should do to the active Canvas's site link. */
export function decideSiteLaunchLink(
  tab: SiteLaunchCanvasTab | undefined,
  launchSiteId: string,
): SiteLaunchLinkDecision {
  if (!tab) return { kind: 'no-canvas' };
  if (tab.site?.id === launchSiteId) return { kind: 'already-linked' };
  if (tab.site) {
    return {
      kind: 'keep-explicit',
      linkedSiteId: tab.site.id,
      linkedSiteName: tab.site.name,
    };
  }
  if (tab.mutationBlocker) return { kind: 'read-only', reason: tab.mutationBlocker };
  return { kind: 'link' };
}

/** The Canvas a Site opened — used to offer Site-scoped starting points on that Canvas only. */
export interface SiteCanvasLaunch {
  readonly siteId: string;
  readonly tabId: string;
}

export interface SiteLaunchLinkPort {
  getActiveTab(): SiteLaunchCanvasTab | undefined;
  setSite(site: EditorSite): void;
  recordLaunch(launch: SiteCanvasLaunch): void;
}

export interface SiteLaunchLinkResult {
  readonly decision: SiteLaunchLinkDecision;
  readonly tabId: string | null;
}

/**
 * Apply the launch link to the active Canvas and remember which Canvas the
 * Site opened. Call it AFTER the entry point has activated the tab it opens.
 */
export function applySiteLaunchLink(
  port: SiteLaunchLinkPort,
  launchSite: EditorSite,
): SiteLaunchLinkResult {
  const tab = port.getActiveTab();
  const decision = decideSiteLaunchLink(tab, launchSite.id);
  if (!tab) return { decision, tabId: null };
  if (decision.kind === 'link') port.setSite(launchSite);
  port.recordLaunch({ siteId: launchSite.id, tabId: tab.id });
  return { decision, tabId: tab.id };
}

/** User-facing notice for a launch decision; null when nothing needs saying. */
export function siteLaunchLinkNotice(
  decision: SiteLaunchLinkDecision,
  launchSiteName: string,
): { tone: 'info'; message: string } | null {
  switch (decision.kind) {
    case 'link':
      return { tone: 'info', message: `Linked this simulation to ${launchSiteName}.` };
    case 'keep-explicit':
      return {
        tone: 'info',
        message: `This simulation stays linked to ${decision.linkedSiteName}. Use Link site to change it.`,
      };
    default:
      return null;
  }
}
